/**
 * Background Prefetch Hook
 *
 * Prefetches data in the background to improve UX when users navigate to
 * features that need this data. Uses React Query's prefetchQuery to populate
 * the cache without blocking the UI.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tradingPairsAPI, connectionsAPI } from '@/services/api';

// Supported exchanges for trading pairs prefetch
const SUPPORTED_TRADING_EXCHANGES = [
  'binance',
  'coinbase',
  'kraken',
  'bitfinex',
  'huobi',
  'okx',
  'bybit',
] as const;

// Normalize exchange name to API-compatible format
const normalizeExchangeId = (venue: string): string => {
  const id = venue.toLowerCase().trim();
  const exchangeMap: Record<string, string> = {
    'coinbase pro': 'coinbase',
    'coinbase prime': 'coinbase',
    'binance us': 'binance',
    'kraken pro': 'kraken',
  };
  return exchangeMap[id] || id.split(/\s+/)[0];
};

interface Connection {
  id: string;
  provider?: string;
  type?: string;
  status?: string;
  metadata?: {
    venue?: string;
    network?: string;
    [key: string]: unknown;
  };
}

interface PrefetchOptions {
  /** Delay before starting prefetch (ms) - allows UI to settle first */
  delay?: number;
  /** Only prefetch if data is older than this (ms) */
  staleTime?: number;
}

/**
 * Hook for prefetching trading pairs for connected exchanges.
 * Call this on pages where exchanges are displayed to warm the cache.
 */
export function usePrefetchTradingPairs(
  exchanges: Connection[] | undefined,
  options: PrefetchOptions = {}
) {
  const queryClient = useQueryClient();
  const { delay = 1000, staleTime = 5 * 60 * 1000 } = options;
  const prefetchedRef = useRef<Set<string>>(new Set());

  const prefetchForExchange = useCallback(
    async (exchangeId: string) => {
      // Skip if already prefetched this session
      if (prefetchedRef.current.has(exchangeId)) return;

      // Check if exchange is supported for trading pairs
      const normalized = normalizeExchangeId(exchangeId);
      if (!SUPPORTED_TRADING_EXCHANGES.includes(normalized as any)) return;

      // Check if we already have fresh data
      const existingData = queryClient.getQueryData(['trading-pairs', normalized]);
      if (existingData) {
        const queryState = queryClient.getQueryState(['trading-pairs', normalized]);
        const dataAge = Date.now() - (queryState?.dataUpdatedAt || 0);
        if (dataAge < staleTime) {
          prefetchedRef.current.add(exchangeId);
          return; // Data is fresh enough
        }
      }

      // Prefetch in background
      try {
        await queryClient.prefetchQuery({
          queryKey: ['trading-pairs', normalized],
          queryFn: () => tradingPairsAPI.getByExchange(normalized, { limit: 500, active: true }),
          staleTime,
        });
        prefetchedRef.current.add(exchangeId);
        console.debug(`[Prefetch] Trading pairs prefetched for ${normalized}`);
      } catch (error) {
        // Silently fail - prefetch is best-effort
        console.debug(`[Prefetch] Failed to prefetch trading pairs for ${normalized}:`, error);
      }
    },
    [queryClient, staleTime]
  );

  // Prefetch trading pairs for all connected exchanges
  useEffect(() => {
    if (!exchanges || exchanges.length === 0) return;

    // Filter to active exchange connections
    const activeExchanges = exchanges.filter(
      (ex) =>
        ex.type === 'exchange' &&
        ['active', 'connected'].includes(ex.status?.toLowerCase() || '')
    );

    if (activeExchanges.length === 0) return;

    // Delay prefetch to let UI settle first
    const timeoutId = setTimeout(() => {
      activeExchanges.forEach((ex) => {
        const exchangeId = ex.metadata?.venue || ex.provider || '';
        if (exchangeId) {
          prefetchForExchange(exchangeId);
        }
      });
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [exchanges, delay, prefetchForExchange]);

  // Return function to manually trigger prefetch for a specific exchange
  return { prefetchForExchange };
}

/**
 * Hook for prefetching a single exchange's trading pairs.
 * Use this when a new exchange is connected.
 */
export function usePrefetchSingleExchange() {
  const queryClient = useQueryClient();

  const prefetch = useCallback(
    async (exchangeId: string) => {
      const normalized = normalizeExchangeId(exchangeId);
      if (!SUPPORTED_TRADING_EXCHANGES.includes(normalized as any)) return;

      try {
        await queryClient.prefetchQuery({
          queryKey: ['trading-pairs', normalized],
          queryFn: () => tradingPairsAPI.getByExchange(normalized, { limit: 500, active: true }),
          staleTime: 5 * 60 * 1000,
        });
        console.debug(`[Prefetch] Trading pairs prefetched for new exchange: ${normalized}`);
      } catch (error) {
        console.debug(`[Prefetch] Failed to prefetch for new exchange ${normalized}:`, error);
      }
    },
    [queryClient]
  );

  return prefetch;
}

/**
 * Hook for prefetching wallet network data when a wallet is connected.
 * Prefetches token lists and network-specific data.
 */
export function usePrefetchWalletData() {
  const queryClient = useQueryClient();

  const prefetch = useCallback(
    async (walletId: string, network: string) => {
      // Prefetch wallet balances in background
      try {
        await queryClient.prefetchQuery({
          queryKey: ['wallet-balances', walletId],
          queryFn: () => connectionsAPI.listBalances(walletId),
          staleTime: 60 * 1000, // 1 minute for balances
        });
        console.debug(`[Prefetch] Wallet balances prefetched for ${walletId}`);
      } catch (error) {
        console.debug(`[Prefetch] Failed to prefetch wallet balances:`, error);
      }

      // Could add network-specific token list prefetch here
      // e.g., prefetch popular tokens for the connected network
    },
    [queryClient]
  );

  return prefetch;
}

/**
 * Hook for app-level prefetching on startup.
 * Prefetches commonly needed data after authentication.
 * @param enabled - Only run prefetch when true (e.g., after authentication)
 */
export function useAppStartupPrefetch(enabled: boolean = true) {
  const queryClient = useQueryClient();
  const hasPrefetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (hasPrefetchedRef.current) return;
    hasPrefetchedRef.current = true;

    // Delay to let critical content load first
    const timeoutId = setTimeout(async () => {
      try {
        // 1. Prefetch connected exchanges list
        const exchanges = await queryClient.fetchQuery({
          queryKey: ['exchange-connections'],
          queryFn: () => connectionsAPI.list({ type: 'exchange' }),
          staleTime: 30 * 1000,
        });

        // 2. Prefetch trading pairs for each connected exchange
        if (Array.isArray(exchanges)) {
          const activeExchanges = exchanges.filter(
            (ex: Connection) =>
              ['active', 'connected'].includes(ex.status?.toLowerCase() || '')
          );

          // Prefetch pairs in parallel, but limit concurrency
          const exchangeIds = activeExchanges
            .map((ex: Connection) => normalizeExchangeId(ex.metadata?.venue || ex.provider || ''))
            .filter((id) => SUPPORTED_TRADING_EXCHANGES.includes(id as any));

          // Unique exchange IDs only
          const uniqueIds = Array.from(new Set(exchangeIds));

          await Promise.allSettled(
            uniqueIds.map((exchangeId) =>
              queryClient.prefetchQuery({
                queryKey: ['trading-pairs', exchangeId],
                queryFn: () => tradingPairsAPI.getByExchange(exchangeId, { limit: 500, active: true }),
                staleTime: 5 * 60 * 1000,
              })
            )
          );

          console.debug(`[Prefetch] App startup: prefetched trading pairs for ${uniqueIds.length} exchanges`);
        }

        // 3. Prefetch strategy library (commonly needed)
        await queryClient.prefetchQuery({
          queryKey: ['strategy-library'],
          queryFn: async () => {
            const { fetchStrategyLibraryEntries } = await import('@/services/strategyLibrary');
            return fetchStrategyLibraryEntries();
          },
          staleTime: 5 * 60 * 1000,
        });

        console.debug('[Prefetch] App startup prefetch complete');
      } catch (error) {
        // Silently fail - prefetch is best-effort
        console.debug('[Prefetch] App startup prefetch error:', error);
      }
    }, 2000); // 2 second delay after mount

    return () => clearTimeout(timeoutId);
  }, [queryClient, enabled]);
}
